import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { 
  ChevronLeft, Plus, Clock, MapPin, Users, 
  LogOut, User, LayoutDashboard
} from 'lucide-react';
import { format, parseISO, isSameDay } from 'date-fns';
import { fr, nlBE, enGB } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getDateLocale = (lang) => lang === 'nl' ? nlBE : lang === 'en' ? enGB : fr;

export default function CoachCalendarPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t, lang } = useTranslation();
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions/coach`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const sessionsForDate = sessions.filter(session => 
    isSameDay(parseISO(session.date), selectedDate)
  );

  const sessionDates = sessions.map(s => parseISO(s.date));

  const formatDateLong = (date) => {
    return format(date, 'EEEE d MMMM yyyy', { locale: getDateLocale(lang) });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/coach/dashboard" className="text-muted-foreground hover:text-foreground">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <Link to="/">
                <img src="/logo.png" alt="Motivya" className="h-8 w-auto" />
              </Link>
            </div>
            
            <div className="flex items-center gap-3">
              <Link to="/coach/dashboard">
                <Button variant="ghost" size="sm">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  {t('coachCal.dashboard')}
                </Button>
              </Link>
              <Link to="/coach/profile">
                <Button variant="ghost" size="sm">
                  <User className="w-4 h-4" />
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold uppercase" data-testid="coach-calendar-title">{t('coachCal.title')}</h1>
            <p className="text-muted-foreground">{t('coachCal.subtitle')}</p>
          </div>
          <Link to="/coach/sessions/new">
            <Button className="btn-secondary" data-testid="new-session-btn">
              <Plus className="w-5 h-5 mr-2" />
              {t('coachCal.newSession')}
            </Button>
          </Link>
        </div>

        <div className="grid lg:grid-cols-[350px,1fr] gap-8">
          <div className="bg-card border border-border rounded-lg p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md"
              modifiers={{
                hasSession: sessionDates,
              }}
              modifiersStyles={{
                hasSession: {
                  backgroundColor: 'hsl(var(--primary) / 0.2)',
                  borderRadius: '50%',
                }
              }}
            />
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-3 h-3 rounded-full bg-primary/30"></div>
                <span>{t('coachCal.dayWithSession')}</span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold mb-4 capitalize">
              {formatDateLong(selectedDate)}
            </h2>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : sessionsForDate.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">{t('coachCal.noSessionDay')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('coachCal.createForDate')}
                </p>
                <Link to="/coach/sessions/new">
                  <Button className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('coachCal.createSession')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {sessionsForDate.map((session) => (
                  <div 
                    key={session.session_id}
                    className="bg-card border border-border rounded-lg p-5 card-hover"
                    data-testid={`calendar-session-${session.session_id}`}
                  >
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            session.status === 'confirmed' ? 'badge-confirmed' : 
                            session.status === 'cancelled' ? 'badge-cancelled' : 'badge-pending'
                          }`}>
                            {session.status === 'confirmed' ? t('coachCal.confirmed') : 
                             session.status === 'cancelled' ? t('coachCal.cancelled') : t('coachCal.pending')}
                          </span>
                          <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
                            {session.category}
                          </span>
                        </div>
                        <h3 className="font-bold text-lg">{session.title}</h3>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {session.start_time} - {session.duration_minutes} min
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {session.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {session.current_participants}/{session.max_participants}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">{session.price}€</div>
                        </div>
                        <Link to={`/coach/sessions/${session.session_id}/edit`}>
                          <Button variant="outline" size="sm">
                            {t('coachCal.edit')}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-xl font-bold mb-4 uppercase">{t('coachCal.allSessions')}</h2>
          
          {sessions.length === 0 ? (
            <p className="text-muted-foreground">{t('coachCal.noSessions')}</p>
          ) : (
            <div className="grid gap-3">
              {sessions
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map((session) => (
                <Link 
                  key={session.session_id}
                  to={`/coach/sessions/${session.session_id}/edit`}
                  className="bg-card/50 border border-border rounded-lg p-4 hover:bg-card transition-colors flex justify-between items-center"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[60px]">
                      <div className="text-2xl font-bold">{format(parseISO(session.date), 'd')}</div>
                      <div className="text-xs text-muted-foreground uppercase">
                        {format(parseISO(session.date), 'MMM', { locale: getDateLocale(lang) })}
                      </div>
                    </div>
                    <div>
                      <div className="font-semibold">{session.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {session.start_time} - {session.location}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      session.status === 'confirmed' ? 'badge-confirmed' : 
                      session.status === 'cancelled' ? 'badge-cancelled' : 'badge-pending'
                    }`}>
                      {session.current_participants}/{session.max_participants}
                    </span>
                    <span className="font-bold text-primary">{session.price}€</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
